import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import type { PasswordRule } from './helpers';

/**
 * The backend's password policy for `POST /api/user/change-password`:
 * 8 to 12 characters, at least one upper-case letter, one lower-case letter,
 * one digit and one special character from THIS list. The server rejects any
 * other symbol (hyphen, space, equals ...) as "no special character", which is
 * why this is a whitelist and not the looser "anything non-alphanumeric" that
 * `newPasswordRule` in `helpers.ts` (still used by Reset Password) applies.
 */
export const PASSWORD_SPECIAL_CHARACTERS = '!@#$%^&*()_+[]{}|;:,.<>?';
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 12;

/**
 * Validator for the New Password control. Raises the same error keys as
 * `newPasswordRule` (`length`, `case`, `number`, `special`) so the strength
 * checklist in the template keeps working, and reports every failing rule at
 * once — as the server does.
 *
 * Pure. An empty value is valid here; required-ness is enforced elsewhere
 * (`passwordFieldValidator`).
 */
export const newPasswordPolicyValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const password: unknown = control.value;
  if (typeof password !== 'string' || password.length === 0) {
    return null;
  }

  const errors: ValidationErrors = {};
  if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH) {
    errors['length'] = true;
  }
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password)) {
    errors['case'] = true;
  }
  if (!/\d/.test(password)) {
    errors['number'] = true;
  }
  if (![...password].some((character) => PASSWORD_SPECIAL_CHARACTERS.includes(character))) {
    errors['special'] = true;
  }
  return Object.keys(errors).length > 0 ? errors : null;
};

/** Checklist copy for the policy above; `rule` matches the validator's error keys. */
export const PASSWORD_POLICY_RULES: PasswordRule[] = [
  { text: '8 to 12 characters', rule: 'length' },
  { text: 'Includes uppercase & lowercase letters', rule: 'case' },
  { text: 'Contains at least one number (0-9)', rule: 'number' },
  {
    text: `Contains at least one special character (${PASSWORD_SPECIAL_CHARACTERS.split('').join(' ')})`,
    rule: 'special',
  },
];
